const BASE_URL =
  import.meta.env.VITE_CENSUS_GEOCODER_BASE_URL ??
  import.meta.env.VITE_CENSUS_GEOCODE_BASE ??
  "/_census/geocoder/locations";
const BENCHMARK = "Public_AR_Current";
const DEFAULT_TIMEOUT_MS = 8000;

export type GeocodeInput = {
  number: string;
  street: string;
  city: string;
  state: string;
  zip: string;
};

export type GeocodeResult = {
  lat: number;
  lng: number;
  matchedAddress: string;
};

export class GeocodeServiceError extends Error {
  constructor(message: string, public readonly cause?: unknown) {
    super(message);
    this.name = "GeocodeServiceError";
  }
}

type CensusResponse = {
  result?: {
    addressMatches?: Array<{
      matchedAddress?: string;
      coordinates?: {
        x?: number;
        y?: number;
      };
    }>;
  };
};

function normalizeValue(value: string): string {
  return value.trim().replace(/\s+/g, " ");
}

function normalizeInput(input: GeocodeInput): GeocodeInput {
  return {
    number: normalizeValue(input.number),
    street: normalizeValue(input.street),
    city: normalizeValue(input.city),
    state: normalizeValue(input.state).toUpperCase(),
    zip: normalizeValue(input.zip),
  };
}

function parseFirstMatch(response: CensusResponse): GeocodeResult | null {
  const match = response.result?.addressMatches?.[0];
  const latitude = match?.coordinates?.y;
  const longitude = match?.coordinates?.x;
  if (typeof latitude !== "number" || typeof longitude !== "number") return null;
  return {
    lat: latitude,
    lng: longitude,
    matchedAddress: match?.matchedAddress ?? "",
  };
}

function buildStructuredUrl(input: GeocodeInput): string {
  const params = new URLSearchParams({
    street: `${input.number} ${input.street}`.trim(),
    city: input.city,
    state: input.state,
    zip: input.zip,
    benchmark: BENCHMARK,
    format: "json",
  });
  return `${BASE_URL}/address?${params.toString()}`;
}

function buildOnelineUrl(input: GeocodeInput): string {
  const params = new URLSearchParams({
    address: `${input.number} ${input.street}, ${input.city}, ${input.state} ${input.zip}`,
    benchmark: BENCHMARK,
    format: "json",
  });
  return `${BASE_URL}/onelineaddress?${params.toString()}`;
}

async function requestWithTimeout(
  url: string,
  timeoutMs: number,
  outerSignal?: AbortSignal,
): Promise<CensusResponse> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  const onAbort = () => controller.abort();
  outerSignal?.addEventListener("abort", onAbort);
  try {
    const response = await fetch(url, {signal: controller.signal});
    if (!response.ok) {
      throw new GeocodeServiceError(`Census geocoder returned HTTP ${response.status}`);
    }
    return (await response.json()) as CensusResponse;
  } catch (error) {
    if (error instanceof GeocodeServiceError) throw error;
    const isAbort = error instanceof DOMException && error.name === "AbortError";
    if (isAbort) {
      throw new GeocodeServiceError("Census geocoder request timed out", error);
    }
    throw new GeocodeServiceError("Census geocoder request failed", error);
  } finally {
    clearTimeout(timeout);
    outerSignal?.removeEventListener("abort", onAbort);
  }
}

export async function geocodeAddress(
  rawInput: GeocodeInput,
  options: {signal?: AbortSignal; timeoutMs?: number} = {},
): Promise<GeocodeResult | null> {
  const input = normalizeInput(rawInput);
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  const structured = await requestWithTimeout(buildStructuredUrl(input), timeoutMs, options.signal);
  const structuredMatch = parseFirstMatch(structured);
  if (structuredMatch) return structuredMatch;

  const oneLine = await requestWithTimeout(buildOnelineUrl(input), timeoutMs, options.signal);
  return parseFirstMatch(oneLine);
}
